import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Card,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Vehicle } from './MilesCarousel';
//import { parseCurrencyString } from '../utils/formatPropertyPrice';
import { MessageType, UserCoverImage, WidgetConfig } from '../types';
import { useState, useEffect, useRef } from 'react';
import { getAssetURL } from '../utils/url';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

interface InfoDetailsProps {
  onOpenCarTypeSelection: () => void;
  onBack: () => void;
  iconUrl: string;
  currentVehicle: Vehicle | null | undefined;
  config: WidgetConfig;
  onSendMessage: (
    message: string,
    metadata?: any,
    messageType?: MessageType,
  ) => void;
  userCoverImage?: UserCoverImage;
  dealerName: string;
}

const InfoDetails = ({
  onOpenCarTypeSelection,
  onBack,
  iconUrl,
  currentVehicle,
  config,
  onSendMessage,
  userCoverImage,
  dealerName,
}: InfoDetailsProps) => {
  const [messageInput, setMessageInput] = useState('');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  //const parsedPrice = parseCurrencyString(String(currentVehicle?.price));

  const quickActions = [
    {
      id: 'is-vehicle-available',
      text: 'Is This Vehicle Still Available?',
      message: 'Is This Vehicle Still Available?',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_vehicle_available.png',
    },
    {
      id: 'payments',
      text: 'What are the payments?',
      message: 'What are the payments?',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_payments.png',
    },
    {
      id: 'test-drive',
      text: 'Schedule a test drive',
      message: 'Schedule a test drive',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_test_drive.png',
    },
    {
      id: 'trade-value',
      text: "What's my trade worth?",
      message: "What's my trade worth?",
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_trade_in.png',
    },
  ];

  // Check if content is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      const element = contentRef.current;
      if (element) {
        const isScrollable = element.scrollHeight > element.clientHeight;
        const isNotAtBottom =
          element.scrollTop + element.clientHeight < element.scrollHeight - 10;
        setShowScrollButton(isScrollable && isNotAtBottom);
      }
    };

    // Check on mount and when content changes
    checkScrollable();

    // Listen for scroll events to hide button when at bottom
    const handleScroll = () => {
      const element = contentRef.current;
      if (element) {
        const isNotAtBottom =
          element.scrollTop + element.clientHeight < element.scrollHeight - 10;
        setShowScrollButton(
          isNotAtBottom && element.scrollHeight > element.clientHeight,
        );
      }
    };

    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }

    // Check on window resize
    window.addEventListener('resize', checkScrollable);

    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', checkScrollable);
    };
  }, [currentVehicle, messageInput]);

  // const handleScheduleTestDrive = () => {
  //   onSendMessage('I would like to book a test drive.', undefined, 'text');
  // };

  // const handleRequestFinance = () => {
  //   onSendMessage('I would like to request financing.', undefined, 'text');
  // };

  const handleSendMessage = () => {
    if (messageInput.trim()) {
      onSendMessage(messageInput, undefined, 'text');
      setMessageInput('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleScrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <Box
        id='smart-shopping-content'
        ref={contentRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          background: `url('https://miles-cdn.qrbuddy.ai/static/img_info_detail_background.png')`,
          backgroundPosition: 'top',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.4)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            textAlign: 'center',
            py: { xs: 2, sm: 1.5 },
            px: 3,
            background: 'linear-gradient(to right, #5E8FF6 0%, #8ABBF6 100%)',
            position: 'relative',
          }}
        >
          <Typography
            variant='h5'
            sx={{
              fontWeight: 700,
              letterSpacing: '0.5px',
              color: 'rgba(255, 255, 255, 1)',
              fontSize: { xs: '1.3rem', sm: '1.3rem' },
            }}
          >
            {dealerName}
          </Typography>

          <Button
            variant='contained'
            sx={{
              fontWeight: 400,
              // color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'none',
              color: '#5E8FF6',
              fontSize: '0.875rem',
              background: 'white',
              borderRadius: '16px',
              py: 0.5,
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              right: 10,
            }}
            onClick={() => {
              window.open(
                `${config?.webUrl}?agentInfoId=${config?.agentId}&hostURL=${window.location.href}`,
                '_blank',
                'noopener,noreferrer',
              );
            }}
          >
            Log In
          </Button>
        </Box>

        <Box p={2}>
          <Typography
            variant='h5'
            sx={{
              // fontWeight: 700,
              // letterSpacing: '0.5px',
              fontSize: { xs: '1.3rem', sm: '1.3rem' },
              whiteSpace: 'pre-line',
            }}
          >
            {'This Vehicle Deserves\nMore Than a Listing'}
          </Typography>

          <Stack spacing={0.5} mt={1}>
            {[
              'Virtual Walkaround',
              'Interior & Exterior Features',
              'Pricing & Payments',
              'Trade-In & Vehicle History',
            ].map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CheckCircleRoundedIcon
                  fontSize='small'
                  sx={{ color: '#D7AC79', fontSize: 12 }}
                />
                <Typography fontSize={14} color='text.primary'>
                  {feature}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Box width={'100%'} textAlign={'center'} my={2}>
            <Box
              component={'img'}
              src={
                userCoverImage
                  ? getAssetURL(
                      config.assetsUrl,
                      userCoverImage?.key,
                      userCoverImage?.url,
                    )
                  : `https://miles-cdn.qrbuddy.ai/static/img_cars_banner.png`
              }
              sx={{
                width: 'auto',
                height: '110px',
                objectFit: 'cover',
                mx: 'auto',
              }}
            />

            <Button
              variant='contained'
              // fullWidth
              onClick={() => {
                onOpenCarTypeSelection();
              }}
              sx={{
                background:
                  'linear-gradient(to bottom, #6A87C3 0.63%, #4468B1 100%)',
                color: '#FFFFFF',
                py: 0.5,
                px: 3,
                mt: 1,
                fontSize: { xs: '0.775rem', sm: '0.775rem' },
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 20,
                '&:hover': {
                  background:
                    'linear-gradient(to bottom, #4468B1 0.63%, #6A87C3 100%)',
                },
              }}
            >
              Enter the Vehicle Showroom
            </Button>
          </Box>

          <Box sx={{}}>
            {/* Message Input */}
            <TextField
              fullWidth
              placeholder='Ask Questions?'
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                  '& fieldset': {
                    borderColor: '#5B8EF580',
                    // borderWidth: 4,
                  },
                  '&:hover fieldset': {
                    borderColor: '#5B8EF580',
                    // borderWidth: 4,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5B8EF580',
                    // borderWidth: 4,
                  },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: 15,
                  color: '#333',
                  '&::placeholder': {
                    color: '#AAA',
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim()}
                      sx={{
                        // bgcolor: '#5B8EF5', //messageInput.trim() ? '#5B8EF5' : '#E0E0E0',
                        color: '#307CFA',
                        borderRadius: 1.5,
                        p: 1,
                        '&:hover': {
                          bgcolor: '#E0E0E0', //messageInput.trim() ? '#4A7DE4' : '#E0E0E0',
                        },
                        '&.Mui-disabled': {
                          // bgcolor: '#5B8EF5',
                          color: '#307CFA',
                        },
                      }}
                    >
                      <SendIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Grid
            container
            spacing={1}
            // gap={1}
            sx={{
              my: 1,
              justifyContent: 'center',
            }}
          >
            {quickActions.map((item) => {
              return (
                <Grid size={6}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      // boxShadow: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1,
                      height: '100%',
                      gap: 1,
                      // cursor: 'pointer',
                    }}
                    onClick={() => {
                      // handleQuickOptionClicked(item);
                      // setMessageInput(item.text);
                    }}
                  >
                    <Box
                      sx={{
                        height: '30px',
                        width: '30px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        component={'img'}
                        src={item.icon}
                        alt={item.text}
                        sx={{
                          width: '30px',
                          height: '30px',
                          objectFit: 'contain',
                          textAlign: 'center',
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        flexGrow: 1,
                        fontSize: '0.768rem',
                        lineHeight: 1.2,
                        fontWeight: 400,
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Typography
            variant='caption'
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              color: 'text.secondary',
              '& span': {
                color: '#4075BE',
                fontSize: '14px',
                fontWeight: 700,
              },
            }}
          >
            Powered by <span>QR Buddy</span>
          </Typography>

          {/* Scroll to Bottom Button */}
          {showScrollButton && (
            <Box
              onClick={handleScrollToBottom}
              sx={{
                position: 'sticky',
                bottom: 16,
                width: '90px',
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#FFD54F',
                color: '#000000',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                textAlign: 'center',
                zIndex: 10,
                '&:hover': {
                  boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  fontSize: '0.9rem',
                }}
              >
                <KeyboardArrowDownIcon />
                More
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      
    </>
  );
};

export default InfoDetails;
